import React, { useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
// import { scaleQuantize } from 'd3-scale';
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import HelpOutlineIcon from "@mui/icons-material/HelpOutlined";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import axios from "axios";
import { AUTH_URL, CHAT_URL } from "../../utils/constants";
// import { truncateText } from '../../utils/utils';
import TruncatedText from "../../utils/TruncatedText";
import { toCamelCase, toSentenceCase, getUserGrievanceStatus } from "../../utils/utils";
import { useAuth } from "../../utils/AuthContext";
import PieCharts from "./PieCharts";
import upDownArrow from "../../assets/images/up-down-arrow.svg";
import { getGrievance } from "../adminDashboard/AdminUser";
import Swal from "sweetalert2";
import AiSummaryModal from "../commonComponent/AiSummaryModal";
import FormattedMarkdown from "../commonComponent/FormattedMarkdown";
const GRADIENT_COLORS = ["#0041C0", "#5f8dea"];

export default function DashboardStatesUser({ dashboardData, data }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [showCommentArea, setShowCommentArea] = useState(false);
  const [isLiked, setIsLiked] = useState(true);
  const [comment, setComment] = useState(""); // New state to store comment
  const [transferAdmin, setTransferAdmin] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchVal = params.get("search");
    if (searchVal) {
      setSearchQuery(searchVal);
    }
  }, [location.search]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [queryUpdate, setQueryUpdate] = useState(true);
  const [aiSummaryOpen, setAiSummaryOpen] = useState(false);
  const [aiSummaryText, setAiSummaryText] = useState("");
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const [pieChartData, setPieChartData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchParam, setSearchParam] = useState("All");

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const { user } = useAuth();

  const normalizeStatus = (status) => {
    const s = (status || "pending").toLowerCase();
    if (s === "pending") return "Pending";
    if (s === "resolved" || s === "closed") return "Resolved";
    if (s === "rejected") return "Rejected";
    if (s === "responded") return "Responded";
    return "Other";
  };

  // Derive counts and groupings
  const stats = useMemo(() => {
    // Use API pie chart data if available
    let statusPieData;

    if (pieChartData) {
      statusPieData = [
        {
          name: "Pending",
          value: pieChartData.pending.count,
          // percentage: pieChartData.pending.percentage,
        },
        {
          name: "In Progress",
          value: pieChartData.inProgress.count,
          // percentage: pieChartData.responded.percentage,
        },
        {
          name: "Resolved",
          value: pieChartData.resolved.count,
          // percentage: pieChartData.responded.percentage,
        },
      ];
    } else {
      // Fallback: Handle old backend format with summary
      const statusSummary =
        Array.isArray(dashboardData.summary) && dashboardData.summary.length
          ? (() => {
            const aggregated = {};
            dashboardData.summary.forEach((item) => {
              const normalizedStatus = normalizeStatus(item.status);
              if (!aggregated[normalizedStatus]) {
                aggregated[normalizedStatus] = {
                  name: normalizedStatus,
                  value: 0,
                  percentage: 0,
                };
              }
              aggregated[normalizedStatus].percentage += item.percentage || 0;
            });
            return Object.values(aggregated).map((item) => ({
              ...item,
              value: item.percentage,
            }));
          })()
          : null;

      const statusCounts = {};
      statusCounts["Pending"] = 0;
      statusCounts["Responded"] = 0;

      data.forEach((item) => {
        const normalizedStatus = normalizeStatus(item.status);
        if (statusCounts[normalizedStatus] !== undefined) {
          statusCounts[normalizedStatus]++;
        }
      });

      statusPieData = statusSummary ?? [
        { name: "Pending", value: statusCounts["Pending"] },
        { name: "Responded", value: statusCounts["Responded"] },
      ];
    }

    const categories = {};
    const normalizedData = data.map((item) => {
      const normalizedStatus = normalizeStatus(item.status);

      const category = item.llm_subcategory || "Other";
      categories[category] = (categories[category] || 0) + 1;

      return {
        ...item,
        normalized_status: normalizedStatus,
      };
    });

    return {
      total: pieChartData?.total ?? dashboardData.total ?? data.length,
      statusPieData,
      categories: Object.entries(categories).map(([name, value]) => ({
        name,
        value,
      })),
      allGrievances: normalizedData,
    };
  }, [data, dashboardData, pieChartData]);

  const filteredGrievances = useMemo(() => {
    let result = stats.allGrievances;
    if (statusFilter !== "All") {
      result = result.filter(
        (item) => item.user_grievance_status === statusFilter,
      );
    }
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      result = result.filter((item) => {
        // Filter only by grievance ID as per request
        return (
          item.grievance_id &&
          item.grievance_id.toLowerCase().includes(lowerQuery)
        );
      });
    }

    if (sortConfig.key) {
      result = [...result].sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case "grievance_id":
            aValue = a.grievance_id || "";
            bValue = b.grievance_id || "";
            break;
          case "reg_date":
            aValue = new Date(a.grievance_submission_timestamp || 0).getTime();
            bValue = new Date(b.grievance_submission_timestamp || 0).getTime();
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [stats.allGrievances, statusFilter, searchQuery, searchParam, sortConfig]);

  const handleViewClick = async (item) => {
    const res = await getGrievance(item.grievance_id);
    if (res) {
      setSelectedRow({ ...item, ...res });
      setIsViewOnly(true); // Set view-only mode
      setIsModalOpen(true);
    }
    // setSelectedRow(item);
    // setIsViewOnly(true); // Set view-only mode
    // setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setSelectedRow(null);
    setAiSummaryOpen(false);
    setAiSummaryText("");
  };

  const handleAiSummary = async () => {
    if (!selectedRow) return;
    setAiSummaryOpen(true);
    setAiSummaryLoading(true);
    setAiSummaryText("");
    try {
      const res = await axios.post(`${CHAT_URL}v1/generate_ai_summary`, {
        grievance_id: selectedRow.grievance_id,
        role: "user",
      }, { headers: { "Content-Type": "application/json" } });
      setAiSummaryText(res.data.summary || "No summary available.");
    } catch (err) {
      setAiSummaryText("Could not generate summary. Please try again.");
    } finally {
      setAiSummaryLoading(false);
    }
  };

  // Fetch pie chart percentages from the new API
  useEffect(() => {
    const fetchPieChartData = async () => {
      try {
        if (data && data.length > 0) {
          const userId = data[0].user_id;
          if (userId) {
            const res = await axios.get(
              `${AUTH_URL}api/pendingrespondedpercentageuser?user_id=${userId}`,
            );
            if (res.data && res.data.success) {
              setPieChartData({
                pending: {
                  count: dashboardData.pending,
                  // percentage: res.data.pending_percentage,
                },
                inProgress: {
                  count: dashboardData.wip,
                  // percentage: res.data.responded_percentage,
                },
                resolved: {
                  count: dashboardData.resolved,
                  // percentage: res.data.responded_percentage,
                },
                total: res.data.total_grievances,
              });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching pie chart data:", error);
        // Fallback to local calculation if API fails
      }
    };

    fetchPieChartData();
  }, [data]);

  const handleLikeClick = (e) => {
    e.preventDefault();
    setIsLiked(true);
    setShowCommentArea(true); // Hide comment area after liking
  };

  const handleDislikeClick = (e) => {
    e.preventDefault();
    setIsLiked(false);
    setShowCommentArea(true); // Show comment area after disliking
  };

  const handleSave1 = async () => {
    setSaveLoading(true);
    try {
      const feedbackData = {
        user_id: user.userId,
        grievance_id: selectedRow.grievance_id, // Assuming you have selectedRow.id for grievance ID
        like_dislike: isLiked, // 1 for like, 0 for dislike
        comments: comment, // The comment entered by the user
      };

      const res = await axios.post(
        `${AUTH_URL}/api/user_grievance_feedback`,
        feedbackData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (res.status === 200) {
        console.log("Feedback saved successfully");
        Swal.fire({
          title: "Feedback Saved Successfully!",
          icon: "success",
        });
        // Optionally, reset the states after saving
        setIsLiked(false);
        setComment("");
        setShowCommentArea(false);
      }
    } catch (error) {
      console.error("Error saving feedback:", error);
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div className="view-inner" style={{ padding: "20px 28px 32px" }}>
      <h2 className="page-title" id="ud-title" style={{ fontSize: "20px", fontWeight: "700", color: "var(--brand-dark)", marginBottom: "20px" }}>My Complaints Overview</h2>
      
      <div className="stat-cards">
        <div className="stat-card c1">
          <div className="top-row">
            <span id="ud-stat1-label">Total Complaints Registered</span>
            <div className="icon-circle"><i className="fa-solid fa-file-lines"></i></div>
          </div>
          <div className="stat-num" id="ud-stat1-num">{stats.total}</div>
        </div>
        <div className="stat-card c2">
          <div className="top-row">
            <span id="ud-stat2-label">Complaints Pending</span>
            <div className="icon-circle"><i className="fa-solid fa-hourglass-half"></i></div>
          </div>
          <div className="stat-num" id="ud-stat2-num">{dashboardData?.pending || 0}</div>
        </div>
        <div className="stat-card c3">
          <div className="top-row">
            <span id="ud-stat3-label">Complaints Resolved</span>
            <div className="icon-circle"><i className="fa-solid fa-check-double"></i></div>
          </div>
          <div className="stat-num" id="ud-stat3-num">{dashboardData?.resolved || 0}</div>
        </div>
      </div>

      <div className="card">
        <h3 id="ud-card-title">Complaints Over Time</h3>
        <input 
          className="search-bar" 
          id="user-search" 
          placeholder="Search by Complaint ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)} 
        />
        <div className="chart-scroll-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th id="ud-th-sno">S.No.</th>
                <th id="ud-th-id" className="cursor-pointer hover:bg-gray-200" onClick={() => requestSort("grievance_id")}>
                  Complaint ID <img src={upDownArrow} alt="sort" className="w-3 h-3 inline ml-1" />
                </th>
                <th id="ud-th-grievance">User Complaint</th>
                <th id="ud-th-dept">Department</th>
                <th>
                  <span id="ud-th-status">Status</span> 
                  <select 
                    className="status-filter ml-2 p-1 border rounded" 
                    id="user-status-filter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="All">All</option>
                    <option value="Open">Open</option>
                    <option value="InProgress">In Progress</option>
                    <option value="Resolved">Resolved</option>
                  </select>
                </th>
                <th id="ud-th-date" className="cursor-pointer hover:bg-gray-200" onClick={() => requestSort("reg_date")}>
                  Date <img src={upDownArrow} alt="sort" className="w-3 h-3 inline ml-1" />
                </th>
                <th id="ud-th-action">Action</th>
              </tr>
            </thead>
            <tbody id="user-table-body">
              {filteredGrievances.length > 0 ? filteredGrievances.map((row, idx) => {
                const status = getUserGrievanceStatus(row) || "Open";
                const statusClass = status.toLowerCase().replace(' ', '-');
                return (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td className="grid-id"><TruncatedText text={row.grievance_id} limit={20} /></td>
                    <td><TruncatedText text={row.grievance_title || row.user_query} limit={48} /></td>
                    <td><TruncatedText text={row.llm_category ? `${row.llm_category} (${row.llm_subcategory || ''})` : 'General Complaint'} limit={25} /></td>
                    <td><span className={`status-badge ${statusClass}`}>{status}</span></td>
                    <td>{new Date(row.grievance_submission_timestamp).toLocaleDateString("en-IN")}</td>
                    <td>
                      <button className="view-eye" onClick={() => handleViewClick(row)}>
                        <i className="fa-solid fa-eye"></i>
                      </button>
                    </td>
                  </tr>
                );
              }) : (
                <tr><td colSpan="7" style={{ textAlign: "center", padding: "24px", color: "var(--text-muted)" }}>No complaints found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && selectedRow && createPortal(
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4 sm:p-8">
            <div className="bg-white w-full sm:w-[80vw] max-w-4xl p-5 sm:p-6 rounded shadow-lg relative my-8">
            <button className="absolute top-4 right-4 text-gray-500 hover:text-red-500" onClick={handleClose}>
              <i className="fa-solid fa-xmark text-xl"></i>
            </button>
            <div className="border-b pb-4 mb-4">
              <h3 className="text-lg font-bold text-gray-800">Complaint Detail</h3>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">User Query</label>
                <div className="text-sm text-gray-800 leading-relaxed bg-gray-50 p-3 rounded border border-gray-200">{selectedRow.user_query}</div>
              </div>
              
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Bot Response / AI Resolution</label>
                <div className="text-sm text-gray-800 leading-relaxed bg-gray-50 p-3 rounded border border-gray-200">
                  Complaint registered and forwarded to the concerned department (<strong>{selectedRow.llm_category}</strong>). The citizen requests an official inspection and prompt resolution as described above.
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Officer Comment / Status Update</label>
                {(selectedRow.officer_grievance_comments || selectedRow.grievance_response) ? (
                  <div className="text-sm text-gray-800 leading-relaxed bg-green-50 p-3 rounded border border-green-200 border-l-4 border-l-green-500 flex items-start gap-2">
                    <i className="fa-solid fa-circle-check text-green-600 mt-1"></i>
                    <span>{selectedRow.officer_grievance_comments || selectedRow.grievance_response}</span>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 italic bg-gray-50 p-3 rounded border border-gray-200">No comments added yet by the officer.</div>
                )}
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Complaint ID</label><div className="text-sm font-semibold text-gray-800">{selectedRow.grievance_id}</div></div>
                <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Department</label><div className="text-sm font-semibold text-gray-800">{selectedRow.llm_category}</div></div>
                <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Sub Category</label><div className="text-sm font-semibold text-gray-800">{selectedRow.llm_subcategory || "General Complaint"}</div></div>
                <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">State</label><div className="text-sm font-semibold text-gray-800">{selectedRow.state || "Uttar Pradesh"}</div></div>
                <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">District</label><div className="text-sm font-semibold text-gray-800">{selectedRow.district || "Sitapur"}</div></div>
                <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Registration Date</label><div className="text-sm font-semibold text-gray-800">{new Date(selectedRow.grievance_submission_timestamp).toLocaleDateString("en-IN")}</div></div>
                <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Status</label><div className="text-sm font-semibold text-gray-800"><span className={`px-2 py-1 rounded-full text-xs text-white font-bold ${(getUserGrievanceStatus(selectedRow)||"Open").toLowerCase() === 'resolved' ? 'bg-green-500' : (getUserGrievanceStatus(selectedRow)||"Open").toLowerCase() === 'in progress' ? 'bg-blue-500' : 'bg-yellow-500'}`}>{getUserGrievanceStatus(selectedRow)||"Open"}</span></div></div>
              </div>
            </div>
            
            <div className="mt-6 pt-4 border-t flex justify-between items-center">
              <button 
                type="button" 
                onClick={handleAiSummary}
                className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center gap-2 text-sm font-semibold shadow-sm transition-colors"
              >
                <AutoAwesomeIcon fontSize="small" /> AI Summary
              </button>
              <button 
                className="px-5 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm font-semibold shadow-sm transition-colors" 
                onClick={handleClose}
              >
                Close
              </button>
            </div>
          </div>
          </div>
        </div>,
        document.body
      )}
      <AiSummaryModal
        isOpen={aiSummaryOpen}
        onClose={() => setAiSummaryOpen(false)}
        summaryText={aiSummaryText}
        loading={aiSummaryLoading}
        grievanceData={selectedRow}
        role="User"
      />
    </div>
  );
}
